import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Bot, CalendarSync, BellRing } from "lucide-react";
import { localizedHref, useMarketingLocale } from "@/i18n/marketing";

/**
 * Home-page promo strip for the fixture-autopilot feature.
 * Logged-out visitors rarely know we crawl their league — this surfaces it.
 */
export function FixtureAutopilot() {
  const locale = useMarketingLocale();
  const href = localizedHref("/fixtures-info", locale);

  const bullets = [
    {
      icon: CalendarSync,
      title: "Auto-pulls your schedule",
      body: "Paste your league URL once. We crawl it and create polls for every fixture.",
    },
    {
      icon: BellRing,
      title: "Flags changes",
      body: "Date moves, venue swaps, cancellations — we spot them and email a digest.",
    },
    {
      icon: Bot,
      title: "Opens polls automatically",
      body: "New fixtures appear with vote links ready to share. Nothing to type.",
    },
  ];

  return (
    <motion.section
      className="py-16 lg:py-20"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-container">
        <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card/60 to-card/40 backdrop-blur-sm p-8 lg:p-12 shadow-xl">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary px-3 py-1 text-xs font-semibold mb-4">
                <Bot className="h-3.5 w-3.5" />
                Fixture Autopilot
              </span>
              <h2 className="heading-2 text-foreground mb-3">
                We crawl your league.{" "}
                <span className="text-primary">You stop typing fixtures.</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 max-w-xl">
                Paste your league's fixtures URL once. Vote.GameOn syncs your
                full season, flags every change, and opens polls automatically
                — so coaches never re-key a schedule again.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={href}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary text-primary-foreground px-5 py-3 text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  See how it works
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-xl border border-border bg-background/60 px-5 py-3 text-sm font-semibold text-foreground hover:bg-background transition-colors"
                >
                  Try it free
                </Link>
              </div>
            </div>

            <ul className="space-y-4">
              {bullets.map((b) => {
                const Icon = b.icon;
                return (
                  <li
                    key={b.title}
                    className="flex gap-4 rounded-2xl border border-border/60 bg-background/60 p-4"
                  >
                    <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-0.5">
                        {b.title}
                      </p>
                      <p className="text-sm text-muted-foreground">{b.body}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
